import { useEffect, useRef, useState } from 'react'
import { useClient } from '@/hooks/useClient'
import type { DiscoveredFacility, DiscoveryKind, EnrichmentView } from './discoveryTypes'

const POLL_MS = 5_000
const MAX_MS = 240_000

export function mergeEnrichment(f: DiscoveredFacility, view?: EnrichmentView): DiscoveredFacility {
  return view ? { ...f, ...view } : f
}

function isPending(f: DiscoveredFacility, view?: EnrichmentView): boolean {
  const v = view ?? f
  return v.nppes.status === 'pending' || v.ai.status === 'pending'
}

/**
 * Enrichment is asynchronous server-side; cards render immediately and this hook
 * fills in NPPES/AI badges as they land. Polls only place ids still pending, every
 * 5 s, for at most 4 min per result set.
 */
export function useEnrichmentPolling(
  facilities: DiscoveredFacility[],
  /** Sent with the poll so it reports exactly as the search did — a lab search
   *  masks an AI status it can never resolve, and an unmasked poll answer would
   *  put the spinner straight back on the row. */
  kind?: DiscoveryKind,
): Record<string, EnrichmentView> {
  const client = useClient()
  const clientRef = useRef(client)
  clientRef.current = client
  const [views, setViews] = useState<Record<string, EnrichmentView>>({})
  const viewsRef = useRef(views)
  viewsRef.current = views
  const key = facilities.map((f) => f.placeId).join(',')

  useEffect(() => {
    setViews({})
    const started = Date.now()
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = async () => {
      if (cancelled) return
      const pending = facilities
        .filter((f) => isPending(f, viewsRef.current[f.placeId]))
        .map((f) => f.placeId)
      if (pending.length === 0 || Date.now() - started > MAX_MS) return
      try {
        const { items } = await clientRef.current.facilityDiscoveryEnrichment({
          placeIds: pending.join(','),
          ...(kind ? { kind } : {}),
        })
        if (!cancelled) setViews((prev) => ({ ...prev, ...items }))
      } catch {
        // transient; try again on the next tick
      }
      if (!cancelled) timer = setTimeout(() => void tick(), POLL_MS)
    }
    timer = setTimeout(() => void tick(), POLL_MS)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
    // `key` stands in for the facilities identity; re-run only when the result set changes.
    // `client` is read via `clientRef` so an unstable client reference (real usage
    // memoizes it, but callers/mocks are not required to) can't restart polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return views
}
